import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, Loader2, AtSign, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Forces Google users (who have no username yet) to claim one before using the app.
export default function UsernameDialog({ open }) {
  const { setUsername } = useAuth();
  const [username, setName] = useState("");
  const [availability, setAvailability] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!username) {
      setAvailability(null);
      return;
    }
    setAvailability("checking");
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/check-username`, { params: { username } });
        setAvailability(data);
      } catch (e) {
        setAvailability(null);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [username]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await setUsername(username.trim());
    setSubmitting(false);
    if (res.ok) {
      toast.success(`You're @${res.user.username} now!`);
    } else {
      setError(res.error);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-md rounded-3xl border-slate-100 p-8 [&>button]:hidden"
        data-testid="username-dialog"
      >
        <DialogTitle className="sr-only">Claim your username</DialogTitle>
        <DialogDescription className="sr-only">
          Choose your globally unique username to finish setting up your account.
        </DialogDescription>
        <div className="text-center mb-5">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-pink-100">
            <Sparkles className="h-6 w-6 text-pink-500" />
          </div>
          <h2 className="font-heading text-2xl font-extrabold text-slate-900">Claim your username</h2>
          <p className="text-sm text-slate-500 mt-1">
            This is your one and only name on Yaprio. Choose wisely!
          </p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="relative">
            <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              autoFocus
              required
              value={username}
              onChange={(e) => setName(e.target.value)}
              placeholder="yourname"
              data-testid="input-claim-username"
              className="pl-9 pr-9 h-12 rounded-xl"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              {availability === "checking" && <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />}
              {availability && availability !== "checking" && availability.available && (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              )}
              {availability && availability !== "checking" && !availability.available && (
                <XCircle className="h-4 w-4 text-red-500" />
              )}
            </span>
          </div>
          {availability && availability !== "checking" && (
            <p className={`text-xs font-medium ${availability.available ? "text-green-600" : "text-red-500"}`}>
              {availability.available ? "Available!" : availability.reason}
            </p>
          )}
          {error && <p className="text-sm text-red-500 font-medium">{error}</p>}
          <Button
            type="submit"
            disabled={submitting || !(availability && availability !== "checking" && availability.available)}
            data-testid="btn-claim-username"
            className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-semibold shadow-lg shadow-pink-500/30"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Claim username"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
